import moment from 'moment-timezone';
import timeZones from './timeZone.json';

export const DEFAULT_TIME_ZONE = 'UTC';
export const USER_TIMEZONE_UPDATED_EVENT = 'user-timezone-updated';
export const TIME_ZONE_OPTIONS = timeZones;

export const getTimeZoneLabel = (value) => {
  if (!value) return '';
  const match = timeZones.find((zone) => zone.value === value);
  return match?.label || value;
};

const SUPPORTED_TIME_ZONE_VALUES = new Set(timeZones.map((zone) => zone.value));

const TIME_ZONE_ALIASES = {
  'Asia/Calcutta': 'Asia/Kolkata',
};

const UNSET_TIME_ZONE_VALUES = new Set(['UTC', 'ETC/UTC', 'GMT', 'ETC/GMT']);

export const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;

const isUnsetTimeZone = (timeZone) => {
  if (!timeZone || typeof timeZone !== 'string') return true;
  const trimmed = timeZone.trim();
  if (!trimmed) return true;
  return UNSET_TIME_ZONE_VALUES.has(trimmed.toUpperCase());
};

const findTimeZoneInList = (timeZone) => {
  if (!timeZone || typeof timeZone !== 'string') return null;

  const trimmed = timeZone.trim();
  if (!trimmed) return null;

  const aliased = TIME_ZONE_ALIASES[trimmed] || trimmed;
  if (SUPPORTED_TIME_ZONE_VALUES.has(aliased)) return aliased;

  if (!moment.tz.zone(trimmed)) return null;

  const exactMatch = timeZones.find(
    (zone) => zone.value === aliased || zone.value === trimmed
  );
  if (exactMatch) return exactMatch.value;

  const offset = moment.tz(trimmed).utcOffset();
  const offsetMatch = timeZones.find(
    (zone) => moment.tz(zone.value).utcOffset() === offset
  );
  return offsetMatch?.value || null;
};

export const resolveTimeZoneOption = (candidate, fallback) => {
  if (!isUnsetTimeZone(candidate)) {
    const resolvedCandidate = findTimeZoneInList(candidate);
    if (resolvedCandidate) return resolvedCandidate;
  }

  const browserTimeZone = getBrowserTimeZone();
  const resolvedBrowser = findTimeZoneInList(browserTimeZone);
  if (resolvedBrowser) return resolvedBrowser;

  if (!isUnsetTimeZone(fallback)) {
    const resolvedFallback = findTimeZoneInList(fallback);
    if (resolvedFallback) return resolvedFallback;
  }

  return timeZones[0]?.value || DEFAULT_TIME_ZONE;
};

export const normalizeTimeZone = (timeZone, fallback = DEFAULT_TIME_ZONE) => {
  const resolvedFallback = resolveTimeZoneOption(fallback, getBrowserTimeZone());
  if (isUnsetTimeZone(timeZone)) {
    return resolvedFallback;
  }

  const resolved = findTimeZoneInList(timeZone);
  return resolved || resolvedFallback;
};

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (error) {
    return {};
  }
};

export const getStoredUserTimeZone = () => {
  const user = getStoredUser();
  return resolveTimeZoneOption(user?.time_zone, getBrowserTimeZone());
};

export const persistUserTimeZone = (timeZone) => {
  const normalized = resolveTimeZoneOption(timeZone, getBrowserTimeZone());
  const user = getStoredUser();
  localStorage.setItem('user', JSON.stringify({ ...user, time_zone: normalized }));
  window.dispatchEvent(
    new CustomEvent(USER_TIMEZONE_UPDATED_EVENT, { detail: { time_zone: normalized } })
  );
  return normalized;
};

export const formatUtcInTimeZone = (
  utcValue,
  timeZone,
  format = 'MMM DD, YYYY, hh:mm A'
) => {
  if (!utcValue) return 'N/A';
  const normalized = normalizeTimeZone(timeZone, getStoredUserTimeZone());
  const parsed = moment.utc(utcValue);
  if (!parsed.isValid()) return 'N/A';
  return parsed.tz(normalized).format(format);
};

export const toUtcIsoFromTimeZone = (localDateTime, timeZone) => {
  const normalized = normalizeTimeZone(timeZone, getStoredUserTimeZone());
  if (!localDateTime) return '';
  const parsed = moment.tz(localDateTime, normalized);
  if (!parsed.isValid()) return '';
  return parsed.utc().toISOString();
};
